import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import NetworkDomainList from "common/values/network-domain/view/network-domain-list";
import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import EntityName from "legal-entities/values/entity-name/entity-name";
import NetworkDomain from "common/values/network-domain/network-domain";
import { styled } from "@mui/material/styles";
import { useDialog } from "app/providers/dialog";

const StyledEditEntityBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  minWidth: 350,
  maxWidth: 500,
}));

const StyledSectionBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledActionsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

interface EditLegalEntityProps {
  legalEntity: LegalEntity;
  onSave?: (updated: LegalEntity) => void;
}

const EditLegalEntity: React.FC<EditLegalEntityProps> = ({ legalEntity, onSave }) => {
  const [initialEntity] = useState(() => legalEntity);
  const [name, setName] = useState(legalEntity.name?.value || "");
  const [domains, setDomains] = useState(
    legalEntity.emailDomains?.map(d => d.value) ?? []
  );

  // Detect if changes have occurred (add, remove, rename)
  const initialName = initialEntity.name?.value || "";
  const initialDomains = (initialEntity.emailDomains?.map(d => d.value) ?? []).sort();
  const currentDomains = [...domains].sort();
  const isUnchanged = name === initialName && JSON.stringify(currentDomains) === JSON.stringify(initialDomains);
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { closeDialog } = useDialog();

  const handleSave = () => {
    if (saving) return;
    try {
      if (!name.trim()) throw new Error("Name is required.");
      EntityName.validate(name);
      setNameError(null);
    } catch (e) {
      setNameError(e instanceof Error ? e.message : String(e));
      return;
    }

    setSaving(true);

    const updated = new LegalEntity(
      legalEntity.id,
      new EntityName(name),
      legalEntity.isClient,
      legalEntity.isVendor,
      domains.map(d => new NetworkDomain(d))
    );
    updated._managerIds = legalEntity._managerIds;
    if (onSave) onSave(updated);
    closeDialog();
  };

  return (
    <StyledEditEntityBox>
      <Typography variant="h6" gutterBottom>Edit Legal Entity</Typography>
      <TextField
        label="Entity Name"
        value={name}
        onChange={e => { setName(e.target.value); setNameError(null); }}
        error={!!nameError}
        helperText={nameError}
        fullWidth
        margin="normal"
      />
      <StyledSectionBox>
        <Typography variant="subtitle1" gutterBottom>Allowed Email Domains</Typography>
        <NetworkDomainList domains={domains} onChange={setDomains} />
      </StyledSectionBox>
      <StyledActionsBox>
        <Button onClick={() => closeDialog()} disabled={saving} variant="outlined">Cancel</Button>     
        <Button onClick={handleSave} disabled={saving || isUnchanged} variant="contained" color="primary">Save</Button>
      </StyledActionsBox>
    </StyledEditEntityBox>
  );
};

export default EditLegalEntity;